import axios from 'axios';
import Homework, { HomeworkWithRelatedData, HomeworkWithTeacherAndSemester, HomeworkStatus, HomeworkTeacherStats, HomeworkTimelineItem } from '@/models/homework';
import store from '@/store';
import { applyErrorHandlers } from '@/decorators/applyErrorHandler';

const API_HOMEWORK_URL = process.env.VUE_APP_API_URL+"/homeworks";
const API_FILE_URL = process.env.VUE_APP_API_URL+"/files";

export default class HomeworkController {
  static async fetchHomeworks(includeArchived = false) {
      const params = includeArchived ? '?include_archived=true' : '';
      const response = await axios.get(`${API_HOMEWORK_URL}/get_homeworks${params}`);
      console.log('Homeworks fetched successfully:', response.data);
      return response.data
        .map(hw => new Homework(hw))
        .sort((a, b) => {
          if (a.archived !== b.archived) return a.archived ? 1 : -1;
          return new Date(a.release_date) - new Date(b.release_date);
        });
  }

  static async fetchHomeworksForStudent() {
      const response = await axios.get(`${API_HOMEWORK_URL}/get_homeworks_after_release_date`);
      console.log('Homeworks fetched successfully:', response.data);
      return response.data
        .map(hw => new Homework(hw))
        .sort((a, b) => new Date(a.release_date) - new Date(b.release_date));
  }

  static async fetchHomeworkById(homeworkId) {
      const response = await axios.get(`${API_HOMEWORK_URL}/get_homework_by_id/${homeworkId}`);
      return new Homework(response.data);
  }

  static async fetchHomeworkByIdWithTeacherAndSemester(homeworkId) {
      const response = await axios.get(`${API_HOMEWORK_URL}/get_homework_by_id_with_teacher_and_semester/${homeworkId}`);
      return new HomeworkWithTeacherAndSemester(response.data);
  }

  static async fetchHomeworkByIdWithRelatedData(homeworkId) {
      const response = await axios.get(`${API_HOMEWORK_URL}/get_homework_by_id_with_related_data/${homeworkId}`);
      const homeworkWithRelatedData = new HomeworkWithRelatedData(response.data);
      // Sort deadlines by date (ascending)
      homeworkWithRelatedData.deadlines.sort((a, b) => new Date(a.date) - new Date(b.date));
      return homeworkWithRelatedData;
  }

  static async createHomework(homeworkData) {
      Homework.validate(homeworkData);
      const response = await axios.post(`${API_HOMEWORK_URL}/create_homework`, homeworkData);
      return new Homework(response.data);
  }

  static async updateHomework(homeworkId, homeworkData) {
      Homework.validate(homeworkData);
      const response = await axios.put(`${API_HOMEWORK_URL}/update_homework/${homeworkId}`, homeworkData);
      return new Homework(response.data);
  }

  static async deleteHomework(homeworkId) {
      const response = await axios.delete(`${API_HOMEWORK_URL}/delete_homework/${homeworkId}`);
      return response.data;
  }

  static async uploadFiles(homeworkId, formData) {
      await axios.post(`${API_FILE_URL}/save_homework_assigments/${store.getters.semesterId}/${homeworkId}`, formData, {
          headers: {
              'Content-Type': 'multipart/form-data'
          }
      });
  }

  static async downloadAllHomeworkFilesForStudent(homeworkId) {
      const response = await axios.get(`${API_FILE_URL}/download_all_homework_files_for_student/${homeworkId}`, {
          responseType: 'blob'
      });
      return response;
  }

  static async downloadFile(filePath) {
      const response = await axios.get(`${API_FILE_URL}/download_file`, {
          params: { relative_file_path: filePath },
          responseType: 'blob'
      });
      return response;
  }

  static async deleteFile(homeworkId, filePath) {
      const response = await axios.delete(`${API_FILE_URL}/delete_file_from_homework/${homeworkId}`, {
          params: { relative_file_path: filePath }
      });
      return response.data;
  }

  // F16b: get homework description diff (latest revision vs current)
  static async getDescriptionDiff(homeworkId) {
    const response = await axios.get(`${API_HOMEWORK_URL}/description_diff/${homeworkId}`);
    return response.data;
  }

  // F12: student homework list with backend-computed status (single call)
  static async fetchHomeworkStatusList() {
      const response = await axios.get(`${API_HOMEWORK_URL}/get_homework_status_list`);
      return response.data.map(item => new HomeworkStatus(item));
  }

  // F12: teacher homework list stats per semester
  static async fetchHomeworkStatsForTeacher(semesterId) {
      const response = await axios.get(`${API_HOMEWORK_URL}/get_homework_stats_for_teacher`, {
          params: { semester_id: semesterId },
      });
      return response.data.map(item => new HomeworkTeacherStats(item));
  }

  // F9: homework timeline (AUTO deadlines + per-deadline state for a student)
  static async fetchTimelineData() {
      const response = await axios.get(`${API_HOMEWORK_URL}/get_timeline_data`);
      return response.data.map(item => new HomeworkTimelineItem(item));
  }
}

applyErrorHandlers(HomeworkController);
